export class BrushOptimizer {
    constructor(brushSystem) {
        this.brushSystem = brushSystem;
        this.texturePainter = brushSystem.texturePainter;
        
        this.canvasPool = [];
        this.maxPoolSize = 10;
        
        this.textureCache = new Map();
        this.maxCacheSize = 50 * 1024 * 1024;
        this.currentCacheSize = 0;
        
        this.renderQueue = [];
        this.isProcessingQueue = false;
        this.batchSize = 5;
        
        this.performanceMetrics = {
            paintOperations: 0,
            averagePaintTime: 0,
            cacheHits: 0,
            cacheMisses: 0
        };
        
        this.initializeOptimizations();
    }
    
    initializeOptimizations() {
        this.setupCanvasPool();
        this.setupTextureStreaming();
        this.setupRenderOptimization();
    }
    
    setupCanvasPool() {
        for (let i = 0; i < this.maxPoolSize; i++) {
            this.canvasPool.push(this.createPooledCanvas());
        }
    }
    
    createPooledCanvas() {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        
        return {
            canvas: canvas,
            context: canvas.getContext('2d'),
            inUse: false,
            lastUsed: Date.now()
        };
    }
    
    getPooledCanvas(width = 256, height = 256) {
        let pooledCanvas = this.canvasPool.find(item => !item.inUse);
        
        if (!pooledCanvas) {
            pooledCanvas = this.createPooledCanvas();
            if (this.canvasPool.length < this.maxPoolSize) {
                this.canvasPool.push(pooledCanvas);
            }
        }
        
        pooledCanvas.inUse = true;
        pooledCanvas.lastUsed = Date.now();
        pooledCanvas.canvas.width = width;
        pooledCanvas.canvas.height = height;
        
        return pooledCanvas;
    }
    
    releasePooledCanvas(pooledCanvas) {
        if (pooledCanvas) {
            pooledCanvas.inUse = false;
            pooledCanvas.lastUsed = Date.now();
            pooledCanvas.context.clearRect(0, 0, pooledCanvas.canvas.width, pooledCanvas.canvas.height);
        }
    }
    
    setupTextureStreaming() {
        this.textureCleanupInterval = setInterval(() => {
            this.cleanupTextureCache();
        }, 30000);
    }
    
    cacheTexture(key, textureData, size) {
        if (this.currentCacheSize + size > this.maxCacheSize) {
            this.evictLeastRecentlyUsed();
        }
        
        this.textureCache.set(key, {
            data: textureData,
            size: size,
            lastAccessed: Date.now(),
            accessCount: 1
        });
        
        this.currentCacheSize += size;
    }
    
    getCachedTexture(key) {
        const cached = this.textureCache.get(key);
        if (cached) {
            cached.lastAccessed = Date.now();
            cached.accessCount++;
            this.performanceMetrics.cacheHits++;
            return cached.data;
        }
        
        this.performanceMetrics.cacheMisses++;
        return null;
    }
    
    evictLeastRecentlyUsed() {
        const entries = Array.from(this.textureCache.entries());
        entries.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
        
        const removeCount = Math.floor(entries.length * 0.25);
        for (let i = 0; i < removeCount; i++) {
            const [key, entry] = entries[i];
            this.textureCache.delete(key);
            this.currentCacheSize -= entry.size;
        }
    }
    
    cleanupTextureCache() {
        const now = Date.now();
        const maxAge = 5 * 60 * 1000;
        
        for (const [key, entry] of this.textureCache.entries()) {
            if (now - entry.lastAccessed > maxAge && entry.accessCount < 3) {
                this.textureCache.delete(key);
                this.currentCacheSize -= entry.size;
            }
        }
    }
    
    setupRenderOptimization() {
        this.renderThrottleDelay = 16;
        this.lastRenderTime = 0;
    }
    
    queueRenderOperation(operation) {
        this.renderQueue.push({
            operation: operation,
            timestamp: Date.now(),
            priority: operation.priority || 1
        });
        
        if (!this.isProcessingQueue) {
            this.processRenderQueue();
        }
    }
    
    async processRenderQueue() {
        if (this.renderQueue.length === 0) {
            this.isProcessingQueue = false;
            return;
        }
        
        this.isProcessingQueue = true;
        
        this.renderQueue.sort((a, b) => b.priority - a.priority);
        
        const batch = this.renderQueue.splice(0, this.batchSize);
        
        const now = performance.now();
        if (now - this.lastRenderTime < this.renderThrottleDelay) {
            await this.sleep(this.renderThrottleDelay - (now - this.lastRenderTime));
        }
        
        for (const item of batch) {
            try {
                await item.operation.execute();
            } catch (error) {
                console.warn('Render operation failed:', error);
            }
        }
        
        this.lastRenderTime = performance.now();
        
        if (this.renderQueue.length > 0) {
            requestAnimationFrame(() => this.processRenderQueue());
        } else {
            this.isProcessingQueue = false;
        }
    }
    
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    
    optimizeBrushStroke(strokeData) {
        const startTime = performance.now();
        
        try {
            const cacheKey = this.generateBrushCacheKey(strokeData);
            let brushTexture = this.getCachedTexture(cacheKey);
            
            if (!brushTexture) {
                brushTexture = this.generateOptimizedBrushTexture(strokeData);
                const textureSize = this.estimateTextureSize(brushTexture);
                this.cacheTexture(cacheKey, brushTexture, textureSize);
            }
            
            const pooledCanvas = this.getPooledCanvas(strokeData.width, strokeData.height);
            
            try {
                this.renderBrushStrokeToCanvas(pooledCanvas, brushTexture, strokeData);
                return pooledCanvas.canvas;
            } finally {
                this.releasePooledCanvas(pooledCanvas);
            }
            
        } finally {
            const paintTime = performance.now() - startTime;
            this.updatePerformanceMetrics(paintTime);
        }
    }
    
    generateBrushCacheKey(strokeData) {
        return `brush_${strokeData.size}_${strokeData.hardness}_${strokeData.opacity}_${strokeData.mode}`;
    }
    
    generateOptimizedBrushTexture(strokeData) {
        const size = Math.min(strokeData.size * 2, 256);
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = size;
        const ctx = canvas.getContext('2d');
        
        if (strokeData.mode === 'erase') {
            this.generateEraseBrush(ctx, size, strokeData);
        } else {
            this.generatePaintBrush(ctx, size, strokeData);
        }
        
        return canvas;
    }
    
    generatePaintBrush(ctx, size, strokeData) {
        const radius = size / 2;
        const gradient = ctx.createRadialGradient(radius, radius, 0, radius, radius, radius);
        
        const hardness = Math.max(0.1, strokeData.hardness);
        gradient.addColorStop(0, `rgba(255, 255, 255, ${strokeData.opacity})`);
        gradient.addColorStop(hardness, `rgba(255, 255, 255, ${strokeData.opacity * 0.5})`);
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, size, size);
    }
    
    generateEraseBrush(ctx, size, strokeData) {
        const radius = size / 2;
        const gradient = ctx.createRadialGradient(radius, radius, 0, radius, radius, radius);
        
        const hardness = Math.max(0.1, strokeData.hardness);
        gradient.addColorStop(0, `rgba(0, 0, 0, ${strokeData.opacity})`);
        gradient.addColorStop(hardness, `rgba(0, 0, 0, ${strokeData.opacity * 0.5})`);
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, size, size);
    }
    
    renderBrushStrokeToCanvas(pooledCanvas, brushTexture, strokeData) {
        const ctx = pooledCanvas.context;
        
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        if (strokeData.mode === 'erase') {
            ctx.globalCompositeOperation = 'destination-out';
        } else {
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = strokeData.color;
        }
        
        ctx.globalAlpha = strokeData.opacity;
        
        ctx.drawImage(
            brushTexture,
            strokeData.x - strokeData.size / 2,
            strokeData.y - strokeData.size / 2,
            strokeData.size,
            strokeData.size
        );
    }
    
    estimateTextureSize(canvas) {
        return canvas.width * canvas.height * 4;
    }
    
    updatePerformanceMetrics(paintTime) {
        this.performanceMetrics.paintOperations++;
        
        const alpha = 0.1;
        this.performanceMetrics.averagePaintTime = 
            this.performanceMetrics.averagePaintTime * (1 - alpha) + paintTime * alpha;
    }
    
    optimizeMemoryUsage() {
        const now = Date.now();
        const maxAge = 2 * 60 * 1000;
        
        this.canvasPool = this.canvasPool.filter(item => {
            if (!item.inUse && now - item.lastUsed > maxAge) {
                return false;
            }
            return true;
        });
        
        if (window.gc) {
            window.gc();
        }
    }
    
    getPerformanceReport() {
        const cacheHitRate = this.performanceMetrics.cacheHits / 
            (this.performanceMetrics.cacheHits + this.performanceMetrics.cacheMisses) * 100;
        
        return {
            paintOperations: this.performanceMetrics.paintOperations,
            averagePaintTime: Math.round(this.performanceMetrics.averagePaintTime * 100) / 100,
            cacheHitRate: Math.round(cacheHitRate * 100) / 100,
            cacheSize: Math.round(this.currentCacheSize / 1024 / 1024 * 100) / 100,
            canvasPoolSize: this.canvasPool.length,
            renderQueueLength: this.renderQueue.length
        };
    }
    
    adjustQualitySettings(frameTime) {
        if (frameTime > 32) {
            this.renderThrottleDelay = Math.min(this.renderThrottleDelay + 2, 33);
            this.batchSize = Math.max(this.batchSize - 1, 1);
        } else if (frameTime < 16) {
            this.renderThrottleDelay = Math.max(this.renderThrottleDelay - 1, 8);
            this.batchSize = Math.min(this.batchSize + 1, 10);
        }
    }
        
    handleWorkerMessage(data) {
        switch (data.type) {
            case 'brushGenerated':
                this.cacheTexture(data.cacheKey, data.brushData, data.size);
                break;
            case 'strokeProcessed':
                this.applyProcessedStroke(data.strokeData);
                break;
        }
    }
    
    dispose() {
        if (this.textureCleanupInterval) {
            clearInterval(this.textureCleanupInterval);
        }
        
        if (this.brushWorker) {
            this.brushWorker.terminate();
        }
        
        this.textureCache.clear();
        this.canvasPool.forEach(item => {
            item.canvas = null;
            item.context = null;
        });
        this.canvasPool = [];
        this.renderQueue = [];
    }
}
